import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import {
    AssignPersonToFolderInput,
    CompensationGridObject,
    CreateEpicrisisInput,
    CreateFolderMedicationInput,
    CreatePersonContactInput,
    CreatePersonInput,
    CreateVoucherInput,
    DeleteEpicrisisInput,
    DeleteFolderMedicationInput,
    DeletePersonContactInput,
    DiagnosisGridObject,
    EpicrisisGridObject,
    EpicrisisObject,
    FolderGridObject,
    FolderMedicationGridObject,
    FolderMedicationObject,
    FolderGridItemObject,
    GetFolderByPersonInput,
    GetFolderInput,
    GetPersonInput,
    GetVoucherInput,
    MarkPersonContactAsPrimaryInput,
    MedicationGridObject,
    OkObject,
    PersonContactGridObject,
    PersonGridItemObject,
    PersonGridObject,
    SetContactOrderInput,
    UnmarkPrimaryPersonContactInput,
    UpdateEpicrisisInput,
    UpdateFolderMedicationInput,
    UpdateInsurancesInput,
    UpdatePersonContactInput,
    UpdatePersonInput,
    UpdateVoucherInput,
    VoucherGridObject,
    VoucherObject,
} from '@it2go/types';
import { GqlService } from './gql.service';
import { filterFrag } from './types/filter.types';
import {
    compensationGridItemFrag,
    diagnosisGridItemFrag,
    folderMedicationGridItemFrag,
    medicationGridItemFrag,
} from './types/medication.types';
import {
    epicrisisGridItemFrag,
    personContactGridItemFrag,
    personFrag,
    personGridItemFrag,
} from './types/person.types';
import { voucherFrag, voucherGridItemFrag } from './types/voucher.types';
import { epicrisisFrag, folderFrag, folderGridItemFrag, folderMedicationFrag } from './types/folder.types';
import { FolderAssignPersonObject } from '@libs/types/src/services/object-type/folder/folder.assign-person-object';
import { IdInput } from '@libs/types/src/common/input-type/id.input';

/** @Deprecated('use root service') */
@Injectable()
export class PersonService extends GqlService {
    getPersons(input: GridInput | null) {
        return this.query<PersonGridObject>(
            `
                query($input: GridInput) {
                    person {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${personGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getPerson(input: GetPersonInput) {
        return this.query<PersonGridItemObject>(`
                query($input: GetPersonInput!) {
                    person {
                        get(input: $input) {
                            ${personFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    createPerson(input: CreatePersonInput) {
        return this.mutate<PersonGridItemObject>(
            `
                mutation($input: CreatePersonInput!) {
                    person {
                        create(input: $input) {
                            ${personFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updatePerson(input: UpdatePersonInput) {
        return this.mutate<PersonGridItemObject>(
            `
                mutation($input: UpdatePersonInput!) {
                    person {
                        update(input: $input) {
                            ${personFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    assignPersonService(input: AssignPersonToFolderInput) {
        return this.mutate<FolderAssignPersonObject>(
            `
                mutation($input: AssignPersonToFolderInput!) {
                    person {
                        assignService(input: $input) {
                            folderId
                        }
                    }
                }
            `,
            { input },
        );
    }

    getEpicrises(input: GridInput | null) {
        return this.query<EpicrisisGridObject>(
            `
                query($input: GridInput) {
                    epicrisis {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${epicrisisGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createEpicrisis(input: CreateEpicrisisInput) {
        return this.mutate<EpicrisisObject>(
            `
                mutation($input: CreateEpicrisisInput!) {
                    epicrisis {
                        create(input: $input) {
                            ${epicrisisFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateEpicrisis(input: UpdateEpicrisisInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdateEpicrisisInput!) {
                    epicrisis {
                        update(input: $input) {
                            ${epicrisisFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteEpicrisis(input: DeleteEpicrisisInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DeleteEpicrisisInput!) {
                    epicrisis {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getFolderMedications(input: GridInput | null) {
        return this.query<FolderMedicationGridObject>(
            `
                query($input: GridInput) {
                    folderMedication {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${folderMedicationGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createFolderMedication(input: CreateFolderMedicationInput) {
        return this.mutate<FolderMedicationObject>(
            `
                mutation($input: CreateFolderMedicationInput!) {
                    folderMedication {
                        create(input: $input) {
                            ${folderMedicationFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteFolderMedication(input: DeleteFolderMedicationInput) {
        return this.mutate(
            `
            mutation($input: DeleteFolderMedicationInput!) {
                folderMedication {
                    delete(input: $input) {
                        ok
                    }
                }
            }
        `,
            { input },
        );
    }

    updateFolderMedication(input: UpdateFolderMedicationInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdateFolderMedicationInput!) {
                    folderMedication {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getPersonContacts(input: GridInput | null, frag = personContactGridItemFrag) {
        return this.query<PersonContactGridObject>(
            `
                query($input: GridInput) {
                    personContact {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    reorderPersonContactOrder(input: SetContactOrderInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: SetContactOrderInput!) {
                    personContact {
                        reorder(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    createPersonContact(input: CreatePersonContactInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: CreatePersonContactInput!) {
                    personContact {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updatePersonContact(input: UpdatePersonContactInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdatePersonContactInput!) {
                    personContact {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    deletePersonContact(input: DeletePersonContactInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DeletePersonContactInput!) {
                    personContact {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    markPersonContactAsPrimary(input: MarkPersonContactAsPrimaryInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: MarkPersonContactAsPrimaryInput!) {
                    personContact {
                        markAsPrimary(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    unmarkPrimaryPersonContact(input: UnmarkPrimaryPersonContactInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UnmarkPrimaryPersonContactInput!) {
                    personContact {
                        unmarkPrimary(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getMedications(input: GridInput | null) {
        return this.query<MedicationGridObject>(
            `
                query($input: GridInput) {
                    medication {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${medicationGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getVouchers(input: GridInput | null) {
        return this.query<VoucherGridObject>(
            `
                query($input: GridInput) {
                    voucher {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${voucherGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    /**
     * @Deprecated()
     */
    getVoucher(input: GetVoucherInput) {
        return this.query<VoucherObject>(
            `
                query($input: GetVoucherInput!) {
                    voucher {
                        get(input: $input) {
                            ${voucherFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteVoucher(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    voucher {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCompensations(input: GridInput | null) {
        return this.query<CompensationGridObject>(
            `
                query($input: GridInput) {
                    compensation {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${compensationGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
            {
                fetchPolicy: 'cache-first',
            },
        );
    }

    getDiagnoses(input: GridInput | null) {
        return this.query<DiagnosisGridObject>(
            `
                query($input: GridInput) {
                    diagnosis {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${diagnosisGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createVoucher(input: CreateVoucherInput) {
        return this.mutate<VoucherObject>(
            `
                mutation($input: CreateVoucherInput!) {
                    voucher {
                        create(input: $input) {
                            ${voucherFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateVoucher(input: UpdateVoucherInput) {
        return this.mutate<VoucherObject>(
            `
                mutation($input: UpdateVoucherInput!) {
                    voucher {
                        update(input: $input) {
                            ${voucherFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateInsurances(input: UpdateInsurancesInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: UpdateInsurancesInput!) {
                    voucher {
                        updateInsurances(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getFolder(input: GetFolderInput) {
        return this.query<FolderGridItemObject>(
            `
                query($input: GetFolderInput!) {
                    folder {
                        get(input: $input) {
                            ${folderFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getFolderByPerson(input: GetFolderByPersonInput) {
        return this.query<FolderGridItemObject>(
            `
                query($input: GetFolderByPersonInput!) {
                    folder {
                        getByPerson(input: $input) {
                            ${folderFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getFolders(input: GridInput) {
        return this.query<FolderGridObject>(
            `
                query($input: GridInput) {
                    folder {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${folderGridItemFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

}
